import i18n from '../i18n';
import { WhenError } from '../request/http';
import { routerManager } from '../router.manager';

/** 库端版本门禁：接口返回 **`latest_device_system_version`** 时展示当前检测版本号 */
export const RBP_RESTAURANT_VERSION_GATE_I18N_KEY = 'rbp_check_permission_db_version_too_low' as const;

/** 库端版本门禁：无 **`latest_device_system_version`** 时不展示「（x 版本）」片段 */
export const RBP_RESTAURANT_VERSION_GATE_NO_CURRENT_I18N_KEY =
    'rbp_check_permission_db_version_too_low_no_current' as const;

/**
 * 版本门禁在 UI 上展示所需的 i18n key 与插值（来自 `GET .../version-check` 响应体）。
 */
export type RestaurantVersionCheckBlockI18nPayload =
    | {
          key: typeof RBP_RESTAURANT_VERSION_GATE_I18N_KEY;
          /** 当前检测版本（`latest_device_system_version`）与最低要求版本 */
          params: {
              current_version: string;
              minimum_version: string;
          };
      }
    | {
          key: typeof RBP_RESTAURANT_VERSION_GATE_NO_CURRENT_I18N_KEY;
          /** 仅最低要求版本 */
          params: {
              minimum_version: string;
          };
      };

/**
 * 当前 SPA 路由路径（兼容 `HashRouter`：优先 `routerManager.history`，否则从 `location.hash` 解析）。
 * 供类组件装饰器等无法使用 `useLocation` 的场景做路径判断。
 */
export function getShellAppPathname(): string {
    /** React Router 注入的 pathname（Hash 模式下为 `#` 后的路径） */
    const fromHistory = routerManager.history?.location?.pathname as string | undefined;
    if (fromHistory && fromHistory.startsWith('/main')) {
        return fromHistory;
    }
    if (typeof window === 'undefined') {
        return '';
    }
    /** 未绑定 history 时的兜底：从 hash 取 `#/main/...` */
    const raw = window.location.hash || '';
    const withoutLeading = raw.startsWith('#') ? raw.slice(1) : raw;
    return (withoutLeading.split('?')[0] || '').trim();
}

/**
 * 是否需在隧道（`checkPermission`）通过后继续调 **`GET /v1/restaurant/{string_id}/version-check`**。
 * 仅 **云 RBP**（`/main/rbp-cloud`）路由使用全局 `HookDecorator` / `CurrentRestaurantDecorator` 时开启；菜单库管理在各自 state 内强制校验。
 * @param pathname - 应用内路径，如 `/main/rbp-cloud/sub-function`
 */
export function shouldEnforceCheckPermissionDbVersionOnShellPath(pathname: string): boolean {
    /** 云 RBP 全链路（含菜品管理等子路径） */
    return pathname.startsWith('/main/rbp-cloud');
}

/**
 * 将版本接口返回的「最低版本」格式化为展示用字符串；空则回退为「未知」的当前语言文案。
 * @param v - 原始字段值（多为 `string`）
 */
function formatRestaurantMinimumVersionLabel(v: unknown): string {
    const trimmed = String(v ?? '').trim();
    if (trimmed) {
        return trimmed;
    }
    return i18n.t('main_page_text_unknown');
}

/**
 * 解析 **`GET /v1/restaurant/{string_id}/version-check`** 成功返回体：当 **`meets_minimum === false`** 时需阻断并提示升级门店侧程序。
 * 与 v3 **`check-permission`** 隧道接口无关；调用方须先完成隧道校验再调版本接口。
 * 有非空 **`latest_device_system_version`** 时用 **`rbp_check_permission_db_version_too_low`**（含 **`current_version`**）；否则用 **`rbp_check_permission_db_version_too_low_no_current`**。
 * @param res - `get_restaurant_version_check` 的返回值（成功时为 JSON 体，非 `error: true` 包装）
 * @returns 若需展示「版本过低」则返回 i18n key 与插值，否则 `null`
 */
export function getRestaurantVersionCheckBlockI18n(res: unknown): RestaurantVersionCheckBlockI18nPayload | null {
    if (!res || typeof res !== 'object') {
        return null;
    }
    if ('error' in res && (res as { error?: boolean }).error === true) {
        return null;
    }
    if ((res as { meets_minimum?: boolean }).meets_minimum !== false) {
        return null;
    }
    const body = res as {
        latest_device_system_version?: unknown;
        minimum_version?: unknown;
    };
    const minimum_version = formatRestaurantMinimumVersionLabel(body.minimum_version);
    const rawCurrent = String(body.latest_device_system_version ?? '').trim();
    if (rawCurrent) {
        return {
            key: RBP_RESTAURANT_VERSION_GATE_I18N_KEY,
            params: {
                current_version: rawCurrent,
                minimum_version,
            },
        };
    }
    return {
        key: RBP_RESTAURANT_VERSION_GATE_NO_CURRENT_I18N_KEY,
        params: { minimum_version },
    };
}

/**
 * 根据后端返回的错误详情，映射为对应的国际化 key
 * @param error 后端返回的错误对象（通常为 WhenError 类型）
 * @param defaultKey 默认回退的国际化 key
 * @returns 国际化 key 字符串
 */
export function mapErrorToI18nKey(
    error: unknown,
    defaultKey = 'business_analysis_tunnel_config_error_connection'
): string {
    // 类型守卫：确保是 WhenError
    if (
        error &&
        typeof error === 'object' &&
        'error' in error &&
        error.error === true
    ) {
        const whenError = error as WhenError;
        const details = whenError.result?.details;

        if (typeof details === 'string') {
            if (details === 'The associated restaurants and tunnel configurations are not equipped.') {
                return 'business_analysis_tunnel_config_error_id_mismatch';
            }
            if (details === 'Restaurant database_host or database_port no exist ') {
                return 'business_analysis_page_msg_no_database_config';
            }
            if (details.includes('restaurant not exist')) {
                return 'business_analysis_restaurant_not_found';
            }
        }
    }

    return defaultKey;
}
